import React from 'react';
import './App.css';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { Provider } from 'react-redux';
import { store } from './store';
import ReduxAuthProvider from './components/ReduxAuthProvider';
import { PermissionsProvider } from './contexts/PermissionsContext';
import ProtectedRoute from './components/ProtectedRoute';
import { useAuth } from './hooks/useAuth';
import NavBar from './components/Layout/NavBar';
import LoginPage from './pages/LoginPage';
import InferenceJobsListPage from './pages/inference/InferenceJobsListPage';
import InferenceJobDetailPage from './pages/inference/InferenceJobDetailPage';
import NewInferenceJobPage from './pages/inference/NewInferenceJobPage';
import FeatureExtractionListPage from './pages/feature-extraction/FeatureExtractionListPage';
import NewFeatureExtractionJobPage from './pages/feature-extraction/NewFeatureExtractionJobPage';
import FeatureExtractionJobDetailPage from './pages/feature-extraction/FeatureExtractionJobDetailPage';
import StudiesListPage from './pages/studies/StudiesListPage';
import StudyDetailPage from './pages/studies/StudyDetailPage';
import DownloadsPage from './pages/downloads/DownloadsPage';
import CredentialsPage from './pages/credentials/CredentialsPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';

const AppRoutes = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={
        isAuthenticated ? <Navigate to="/studies" replace /> : <LoginPage />
      } />
      <Route path="/" element={<Navigate to="/studies" replace />} />

      {/* Experiments - requires 'experiments' permission */}
      <Route
        path="/jobs"
        element={
          isAuthenticated ? (
            <ProtectedRoute pageCode="experiments">
              <InferenceJobsListPage />
            </ProtectedRoute>
          ) : <Navigate to="/login" replace />
        }
      />
      <Route
        path="/jobs/new"
        element={
          isAuthenticated ? (
            <ProtectedRoute pageCode="experiments">
              <NewInferenceJobPage />
            </ProtectedRoute>
          ) : <Navigate to="/login" replace />
        }
      />
      <Route
        path="/jobs/:id"
        element={
          isAuthenticated ? (
            <ProtectedRoute pageCode="experiments">
              <InferenceJobDetailPage />
            </ProtectedRoute>
          ) : <Navigate to="/login" replace />
        }
      />

      {/* Feature Extraction - requires 'feature_extraction' permission */}
      <Route
        path="/feature-extraction"
        element={
          isAuthenticated ? (
            <ProtectedRoute pageCode="feature_extraction">
              <FeatureExtractionListPage />
            </ProtectedRoute>
          ) : <Navigate to="/login" replace />
        }
      />
      <Route
        path="/feature-extraction/new"
        element={
          isAuthenticated ? (
            <ProtectedRoute pageCode="feature_extraction">
              <NewFeatureExtractionJobPage />
            </ProtectedRoute>
          ) : <Navigate to="/login" replace />
        }
      />
      <Route
        path="/feature-extraction/:id"
        element={
          isAuthenticated ? (
            <ProtectedRoute pageCode="feature_extraction">
              <FeatureExtractionJobDetailPage />
            </ProtectedRoute>
          ) : <Navigate to="/login" replace />
        }
      />

      {/* Studies - requires 'studies' permission */}
      <Route
        path="/studies"
        element={
          isAuthenticated ? (
            <ProtectedRoute pageCode="studies">
              <StudiesListPage />
            </ProtectedRoute>
          ) : <Navigate to="/login" replace />
        }
      />
      <Route
        path="/studies/:id"
        element={
          isAuthenticated ? (
            <ProtectedRoute pageCode="studies">
              <StudyDetailPage />
            </ProtectedRoute>
          ) : <Navigate to="/login" replace />
        }
      />

      {/* Downloads - requires 'downloads' permission */}
      <Route
        path="/downloads"
        element={
          isAuthenticated ? (
            <ProtectedRoute pageCode="downloads">
              <DownloadsPage />
            </ProtectedRoute>
          ) : <Navigate to="/login" replace />
        }
      />

      {/* Credentials - available to all authenticated users */}
      <Route
        path="/credentials"
        element={
          isAuthenticated ? <CredentialsPage /> : <Navigate to="/login" replace />
        }
      />

      {/* Admin - requires 'admin' permission (staff only) */}
      <Route
        path="/admin"
        element={
          isAuthenticated ? (
            <ProtectedRoute pageCode="admin">
              <AdminDashboardPage />
            </ProtectedRoute>
          ) : <Navigate to="/login" replace />
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <Provider store={store}>
      <ReduxAuthProvider>
        <PermissionsProvider>
          <BrowserRouter>
            <NavBar />
            <AppRoutes />
          </BrowserRouter>
        </PermissionsProvider>
      </ReduxAuthProvider>
    </Provider>
  );
}

export default App;
